/**
 * Twee Validator
 * Validation functions for Twee code.
 * 
 * @module utils/compiler/validateTwee
 */

import { parseTweeText, extractLinks } from './parseTwee';

/**
 * Validate Twee code structure
 * @param {string} twee - Twee formatted text
 * @returns {{valid: boolean, errors: string[], warnings: string[], passages: number, links: number}}
 */
export const validateTwee = (twee) => {
  const errors = [];
  const warnings = [];
  const passages = parseTweeText(twee);

  if (passages.length === 0) {
    errors.push('No passages found. Each passage should start with "::"');
    return { valid: false, errors, warnings, passages: 0, links: 0 };
  }

  const hasStart = passages.some(p => p.name === 'Start');
  if (!hasStart && passages.filter(p => p.name !== 'StoryTitle').length > 0) {
    warnings.push('No "Start" passage found. First passage will be used.');
  }

  const passageNames = new Set(passages.map(p => p.name));
  let totalLinks = 0;
  const brokenLinks = [];

  passages.forEach(passage => {
    if (passage.name === 'StoryTitle') return;
    const links = extractLinks(passage.content);
    totalLinks += links.length;
    links.forEach(link => {
      if (!passageNames.has(link.target) && !link.target.startsWith('URL:')) {
        brokenLinks.push({ from: passage.name, to: link.target });
      }
    });
  });

  if (brokenLinks.length > 0) {
    warnings.push(`Found ${brokenLinks.length} broken link(s)`);
    brokenLinks.slice(0, 3).forEach(bl => {
      warnings.push(`  "${bl.from}" → "${bl.to}"`);
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    passages: passages.filter(p => p.name !== 'StoryTitle').length,
    links: totalLinks,
  };
};

/**
 * Filter Twee by node level
 * @param {string} tweeText - Twee code
 * @param {Object} config - { hideH1Nodes, hideH2Nodes }
 * @returns {string}
 */
export const filterTweeByNodeLevel = (tweeText, config) => {
  if (!config || (!config.hideH1Nodes && !config.hideH2Nodes)) return tweeText;

  const lines = tweeText.split('\n');
  const result = [];
  let passageContent = [];
  let currentPassage = null;
  let skipPassage = false;

  for (const line of lines) {
    if (line.startsWith(':: ')) {
      if (currentPassage && !skipPassage) result.push(...passageContent);
      passageContent = [line];
      currentPassage = line;
      skipPassage = false;
    } else if (currentPassage) {
      passageContent.push(line);
      if (line.includes('<!--@nodeLevel:')) {
        const match = line.match(/<!--@nodeLevel:\s*(\w+)\s*-->/);
        if (match) {
          const level = match[1];
          if ((config.hideH1Nodes && level === 'h1') || (config.hideH2Nodes && level === 'h2')) {
            skipPassage = true;
          }
        }
      }
    } else {
      result.push(line);
    }
  }

  if (currentPassage && !skipPassage) result.push(...passageContent);
  return result.join('\n');
};
